// Shared export helpers for Excel (.xlsx) and PDF reports.
// Used by Kas RT and Iuran Warga pages.
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatIDR = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

/**
 * Export to Excel (.xlsx)
 * @param {string} filename e.g. "kas-rt-2026-08.xlsx"
 * @param {string} sheetName e.g. "Kas RT Agustus 2026"
 * @param {Array<string>} headers column titles
 * @param {Array<Array>} rows 2D array of row values
 * @param {Array<Array>} [summaryRows] optional summary rows appended after
 */
export const exportToExcel = ({ filename, sheetName, headers, rows, summaryRows = [] }) => {
  const wsData = [headers, ...rows];
  if (summaryRows.length) {
    wsData.push([]);
    summaryRows.forEach((r) => wsData.push(r));
  }
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  // Auto-fit column widths (min 10, max 60)
  const colWidths = headers.map((h, ci) => {
    let max = String(h).length;
    for (const row of rows) {
      const v = row[ci];
      const len = v == null ? 0 : String(v).length;
      if (len > max) max = len;
    }
    return { wch: Math.min(60, Math.max(10, max + 2)) };
  });
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
};

/**
 * Export to Word (.doc via HTML — MS Word opens HTML natively)
 * Simple approach that requires no additional npm packages.
 */
export const exportToWord = ({ filename, title, subtitle = "", orgName = "WARGA RT DM RESIDENCE", headers, rows, summary = [], logoUrl = "" }) => {
  const summaryHtml = summary.length
    ? `<table style="width:100%;margin-bottom:12px;border-collapse:collapse;">${summary.map(([k, v]) => `<tr><td style="padding:3px 8px;font-weight:bold;width:180px;">${k}</td><td style="padding:3px 8px;">${v}</td></tr>`).join("")}</table>`
    : "";
  const logoHtml = logoUrl ? `<div style="text-align:center;margin-bottom:6px;"><img src="${logoUrl}" style="height:64px;" /></div>` : "";
  const bodyHtml = `
    ${logoHtml}
    <h2 style="text-align:center;margin:0;">${orgName}</h2>
    <h3 style="text-align:center;margin:4px 0 12px 0;">${title}</h3>
    ${subtitle ? `<p style="text-align:center;font-size:11px;color:#555;">${subtitle}</p>` : ""}
    ${summaryHtml}
    <table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead><tr style="background:#047857;color:#fff;">${headers.map((h) => `<th style="border:1px solid #999;padding:4px 6px;">${h}</th>`).join("")}</tr></thead>
      <tbody>${rows.map((r) => `<tr>${r.map((c) => `<td style="border:1px solid #999;padding:3px 6px;">${c == null ? "" : String(c)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>
    <p style="margin-top:14px;font-size:9px;color:#888;">Dicetak: ${new Date().toLocaleString("id-ID")}</p>
  `;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${title}</title></head><body>${bodyHtml}</body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/**
 * Import from Excel/CSV file. Returns array of row objects keyed by column headers.
 * Accepts a File object from <input type=file>.
 */
export const importFromExcel = async (file) => {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
};

/**
 * Export to PDF (landscape by default, using autoTable for the data grid)
 * @param {object} opts
 *   filename    string
 *   title       string        - big header
 *   subtitle    string        - optional subheader
 *   orgName     string        - app/community name (e.g. "WARGA RT DM RESIDENCE")
 *   headers     Array<string>
 *   rows        Array<Array>  - 2D data
 *   summary     Array<[label, value]>  - key/value pairs shown above the table
 *   orientation "portrait" | "landscape"
 *   footer      string        - printed at bottom of every page
 *   numericCols Array<number> - column indexes to right-align
 */
export const exportToPDF = ({
  filename,
  title,
  subtitle = "",
  orgName = "WARGA RT DM RESIDENCE",
  headers,
  rows,
  summary = [],
  orientation = "landscape",
  footer = "",
  numericCols = [],
  logoDataUrl = "",
}) => {
  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header (optional centered logo above org name)
  let cursorY;
  if (logoDataUrl) {
    try { doc.addImage(logoDataUrl, "PNG", pageWidth / 2 - 24, 14, 48, 48); } catch (e) { /* logo optional */ }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(orgName, pageWidth / 2, 76, { align: "center" });
    doc.setFontSize(12);
    doc.text(title, pageWidth / 2, 94, { align: "center" });
    if (subtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(90);
      doc.text(subtitle, pageWidth / 2, 110, { align: "center" });
      doc.setTextColor(0);
    }
    cursorY = subtitle ? 126 : 114;
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(orgName, pageWidth / 2, 40, { align: "center" });
    doc.setFontSize(12);
    doc.text(title, pageWidth / 2, 58, { align: "center" });
    if (subtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(90);
      doc.text(subtitle, pageWidth / 2, 74, { align: "center" });
      doc.setTextColor(0);
    }
    cursorY = subtitle ? 90 : 78;
  }

  // Summary rows (key/value pairs)
  if (summary.length) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const colWidth = (pageWidth - 80) / 2;
    summary.forEach(([label, value], idx) => {
      const x = 40 + (idx % 2) * colWidth;
      const y = cursorY + Math.floor(idx / 2) * 14;
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value), x + 110, y);
    });
    cursorY += Math.ceil(summary.length / 2) * 14 + 8;
  }

  const numericSet = new Set(numericCols);
  const columnStyles = {};
  numericCols.forEach((c) => {
    columnStyles[c] = { halign: "right" };
  });

  autoTable(doc, {
    startY: cursorY,
    head: [headers],
    body: rows.map((r) =>
      r.map((cell, ci) => (numericSet.has(ci) && typeof cell === "number" ? formatIDR(cell) : cell)),
    ),
    theme: "grid",
    headStyles: { fillColor: [4, 120, 87], textColor: 255, fontSize: 9, halign: "center" },
    bodyStyles: { fontSize: 8, cellPadding: 3 },
    alternateRowStyles: { fillColor: [240, 253, 250] },
    columnStyles,
    margin: { left: 30, right: 30, bottom: 40 },
    didDrawPage: () => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageNum = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(120);
      if (footer) doc.text(footer, 40, pageHeight - 20);
      doc.text(
        `Halaman ${pageNum} • Dicetak ${new Date().toLocaleString("id-ID")}`,
        pageWidth - 40,
        pageHeight - 20,
        { align: "right" },
      );
      doc.setTextColor(0);
    },
  });

  doc.save(filename);
};
